"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Truck, Plane, PackageCheck, Clock, ShieldCheck, TriangleAlert as AlertTriangle, Warehouse, Forklift, FileText, PhoneCall } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
const tiers = [{
  tierId: "standard-ground",
  iconName: Truck,
  category: "PARCEL / UNDER 70 LBS",
  title: "Standard Ground Delivery",
  timeline: "2 to 5 Business Days",
  summary: "Ideal for hand tools, power tools, replacement parts, and maintenance gear under 70 lbs.",
  handling: "Dispatched within 24 hours (Mon-Fri)",
  carrier: "UPS Ground / FedEx Home Delivery",
  cost: "Free on orders over $99 (or flat $9.95)"
}, {
  tierId: "expedited-air",
  iconName: Plane,
  category: "PRIORITY / TIME-CRITICAL",
  title: "Expedited Air Freight",
  timeline: "1 to 2 Business Days",
  summary: "Priority parcel routing for critical job-site repairs, urgent diagnostic instruments, and tooling.",
  handling: "Same-day dispatch if placed by 1:00 PM EST",
  carrier: "FedEx 2Day / UPS Next Day Air",
  cost: "Calculated dynamically at checkout"
}, {
  tierId: "ltl-freight",
  iconName: Forklift,
  category: "HEAVY FREIGHT / OVER 150 LBS",
  title: "LTL Heavy Machinery Delivery",
  timeline: "4 to 7 Business Days",
  summary: "Dedicated palletized freight for heavy workshop machinery, commercial lifts, and tool chests.",
  handling: "Requires 48-hour carrier scheduling",
  carrier: "R&L Carriers / Estes Express / Old Dominion",
  cost: "Flat $149 freight fee (includes liftgate)"
}];
const matrixData = [{
  classCode: "T-01",
  weightRange: "0 - 15 lbs",
  method: "Parcel Ground",
  carriers: "USPS Priority / UPS",
  window: "Same / Next Day",
  rate: "Free > $99 ($8.95 flat)"
}, {
  classCode: "T-02",
  weightRange: "16 - 70 lbs",
  method: "Heavy Parcel Ground",
  carriers: "UPS / FedEx Ground",
  window: "1 Business Day",
  rate: "Free > $99 ($14.50 flat)"
}, {
  classCode: "T-03",
  weightRange: "71 - 150 lbs",
  method: "Expedited Multi-Box / Overweight",
  carriers: "FedEx Freight Economy",
  window: "1 - 2 Business Days",
  rate: "$45.00 Flat Rate"
}, {
  classCode: "T-04",
  weightRange: "151 - 850 lbs",
  method: "LTL Palletized + Liftgate",
  carriers: "R&L / Estes Freight",
  window: "2 - 3 Business Days",
  rate: "$149.00 Flat Freight"
}, {
  classCode: "T-05",
  weightRange: "850+ lbs",
  method: "Dedicated Flatbed / Dock Delivery",
  carriers: "Specialized Freight Logistics",
  window: "3 - 5 Business Days",
  rate: "Custom Quote at Checkout"
}];
const protocolSteps = [{
  stepNum: "01",
  name: "Dispatch Notice & Tracking",
  desc: "Once palletized, a Bill of Lading (BOL) tracking number and terminal dispatch confirmation are sent via email."
}, {
  stepNum: "02",
  name: "Carrier Call to Schedule",
  desc: "The freight dispatcher contacts you 24 hours in advance to schedule a 4-hour delivery window at your location."
}, {
  stepNum: "03",
  name: "Delivery & Unloading",
  desc: "Standard heavy freight includes hydraulic liftgate service to lower equipment to ground level at your curb or driveway."
}, {
  stepNum: "04",
  name: "Inspection & Sign-Off",
  desc: "Inspect crate exterior before signing the BOL. Note any visible damage on delivery paperwork immediately."
}];
export default function Shipping() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.06
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 14
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.24,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="shipping-policy" data-nav="dark" className="bg-background text-foreground py-16 px-4 md:px-8 lg:px-12 border-b border-border">
      <div className="mx-auto max-w-7xl space-y-16">
        
        {/* Section Header */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <span className="h-2 w-2 bg-primary rounded-full" />
            <span className="font-mono text-xs md:text-sm font-bold tracking-wider text-muted-foreground uppercase">
              Logistics & Fulfillment Specifications
            </span>
          </div>

          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 border-l-3 border-primary pl-4 md:pl-6">
            <div className="space-y-2 max-w-3xl">
              <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground uppercase">
                Shipping & Delivery Policy
              </h1>
              <p className="font-sans text-base sm:text-lg text-muted-foreground leading-relaxed">
                Review nationwide dispatch protocols, parcel delivery classes, and heavy freight procedures for industrial tools and workshop machinery.
              </p>
            </div>

            <div className="shrink-0 flex items-center gap-3">
              <Badge variant="outline" className="font-mono text-xs border-border bg-card px-3 py-1 text-foreground">
                Origin: Orange, CA 92868
              </Badge>
              <Badge variant="secondary" className="font-mono text-xs px-3 py-1">
                48 Contiguous States
              </Badge>
            </div>
          </div>
        </div>

        {/* 1. Standard & Freight Delivery Tiers Grid */}
        <div className="space-y-6">
          <div className="flex items-center justify-between border-b border-border pb-3">
            <div className="flex items-center gap-3">
              <PackageCheck className="size-5 text-primary" />
              <h2 className="font-serif text-xl sm:text-2xl font-bold uppercase tracking-wide text-foreground">
                Fulfillment Classes & Delivery Tiers
              </h2>
            </div>
            <span className="font-mono text-xs text-muted-foreground hidden sm:inline-block">
              SPEC // SEC-01
            </span>
          </div>

          <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
          once: true,
          amount: 0.15
        }} className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {tiers.map((tier, i) => {
            const Icon = tier.iconName;
            return <motion.div key={tier.tierId} variants={itemVariants} data-index={i}>
                  <Card className="h-full border border-border bg-card rounded-sm transition-[border-color,box-shadow] duration-200 hover:border-primary hover:shadow-lg flex flex-col justify-between">
                    <CardHeader className="p-6 space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="p-2.5 rounded-sm bg-muted text-primary border border-border">
                          <Icon className="size-5" />
                        </div>
                        <Badge variant="outline" className="font-mono text-[11px] uppercase tracking-wider border-border text-muted-foreground">
                          {tier.category}
                        </Badge>
                      </div>

                      <div className="space-y-1.5">
                        <CardTitle className="font-serif text-xl font-bold uppercase text-card-foreground">
                          {tier.title}
                        </CardTitle>
                        <div className="flex items-center gap-2 font-mono text-sm text-primary font-semibold">
                          <Clock className="size-4" />
                          <span>{tier.timeline}</span>
                        </div>
                      </div>

                      <p className="text-sm text-muted-foreground font-sans leading-normal">
                        {tier.summary}
                      </p>
                    </CardHeader>

                    <CardContent className="p-6 pt-0 border-t border-border mt-auto space-y-3">
                      <div className="pt-4 space-y-2 text-xs font-mono">
                        <div className="flex justify-between items-center text-muted-foreground">
                          <span>Dispatch Speed:</span>
                          <span className="text-foreground font-medium">{tier.handling}</span>
                        </div>
                        <div className="flex justify-between items-center text-muted-foreground">
                          <span>Carrier Routing:</span>
                          <span className="text-foreground font-medium">{tier.carrier}</span>
                        </div>
                        <div className="flex justify-between items-center pt-2 border-t border-border text-foreground font-bold">
                          <span>Tier Pricing:</span>
                          <span className="text-primary">{tier.cost}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>;
          })}
          </motion.div>
        </div>

        {/* 2. Transit Timeline & Rates Table */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 14
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.24,
        ease: [0.16, 1, 0.3, 1]
      }} className="space-y-6">
          <div className="flex items-center justify-between border-b border-border pb-3">
            <div className="flex items-center gap-3">
              <FileText className="size-5 text-primary" />
              <h2 className="font-serif text-xl sm:text-2xl font-bold uppercase tracking-wide text-foreground">
                Domestic Rate Matrix & Dispatch Specifications
              </h2>
            </div>
            <span className="font-mono text-xs text-muted-foreground hidden sm:inline-block">
              MATRIX // SPEC-02
            </span>
          </div>

          <div className="border border-border bg-card rounded-sm overflow-hidden shadow-sm">
            <Table>
              <TableHeader className="bg-muted/60 border-b border-border">
                <TableRow className="hover:bg-transparent border-border">
                  <TableHead className="font-mono text-xs text-muted-foreground uppercase font-bold py-3 px-4">Class</TableHead>
                  <TableHead className="font-mono text-xs text-muted-foreground uppercase font-bold py-3 px-4">Weight Bracket</TableHead>
                  <TableHead className="font-mono text-xs text-muted-foreground uppercase font-bold py-3 px-4">Fulfillment Method</TableHead>
                  <TableHead className="font-mono text-xs text-muted-foreground uppercase font-bold py-3 px-4">Primary Carriers</TableHead>
                  <TableHead className="font-mono text-xs text-muted-foreground uppercase font-bold py-3 px-4">Dispatch Window</TableHead>
                  <TableHead className="font-mono text-xs text-muted-foreground uppercase font-bold py-3 px-4 text-right">Standard Rate</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {matrixData.map((row, i) => <TableRow key={row.classCode} data-index={i} className="border-border hover:bg-accent/50 transition-colors">
                    <TableCell className="font-mono text-xs font-bold text-primary py-3 px-4">
                      {row.classCode}
                    </TableCell>
                    <TableCell className="font-sans text-sm font-medium text-foreground py-3 px-4">
                      {row.weightRange}
                    </TableCell>
                    <TableCell className="font-sans text-sm text-foreground py-3 px-4">
                      {row.method}
                    </TableCell>
                    <TableCell className="font-sans text-xs text-muted-foreground py-3 px-4">
                      {row.carriers}
                    </TableCell>
                    <TableCell className="font-mono text-xs text-foreground py-3 px-4">
                      {row.window}
                    </TableCell>
                    <TableCell className="font-mono text-sm font-semibold text-primary py-3 px-4 text-right">
                      {row.rate}
                    </TableCell>
                  </TableRow>)}
              </TableBody>
            </Table>
          </div>
          <p className="font-mono text-xs text-muted-foreground">
            * Orders placed after 1:00 PM EST ship on the following business day. Heavy machinery freight orders require customer phone confirmation before warehouse release.
          </p>
        </motion.div>

        {/* 3. Heavy Equipment & Job Site Delivery Protocol */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 14
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.24,
        ease: [0.16, 1, 0.3, 1]
      }} className="space-y-6">
          <div className="flex items-center justify-between border-b border-border pb-3">
            <div className="flex items-center gap-3">
              <Warehouse className="size-5 text-primary" />
              <h2 className="font-serif text-xl sm:text-2xl font-bold uppercase tracking-wide text-foreground">
                Heavy Equipment & Freight Receiving Protocol
              </h2>
            </div>
            <span className="font-mono text-xs text-muted-foreground hidden sm:inline-block">
              PROTOCOL // SPEC-03
            </span>
          </div>

          <div className="border border-border bg-card p-6 md:p-8 rounded-sm space-y-8">
            <Alert className="border-border bg-muted/40 text-foreground">
              <AlertTriangle className="size-5 text-primary" />
              <AlertTitle className="font-serif uppercase text-base font-bold text-foreground">
                Commercial & Residential Freight Requirements
              </AlertTitle>
              <AlertDescription className="text-muted-foreground text-sm font-sans mt-1">
                All workshop machinery and gear over 150 lbs is shipped via commercial semi-truck. Ensure delivery locations accommodate 53-foot tractor-trailers. Hydraulic liftgate service lowers pallets to curbside level; drivers cannot move freight inside garages or workshops.
              </AlertDescription>
            </Alert>

            {/* 4-Step Receiving Workflow */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {protocolSteps.map((step, i) => <div key={step.stepNum} data-index={i} className="border border-border bg-background p-5 rounded-sm relative flex flex-col justify-between">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-2xl font-bold text-primary">
                        {step.stepNum}
                      </span>
                      <ShieldCheck className="size-4 text-muted-foreground" />
                    </div>
                    <h3 className="font-serif text-lg font-bold uppercase text-foreground">
                      {step.name}
                    </h3>
                    <p className="font-sans text-xs text-muted-foreground leading-relaxed">
                      {step.desc}
                    </p>
                  </div>
                </div>)}
            </div>

            {/* Support Callout Footer */}
            <div className="border-t border-border pt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="space-y-1">
                <h4 className="font-serif text-base font-bold uppercase text-foreground">
                  Need custom dock coordination or expedited freight?
                </h4>
                <p className="font-sans text-xs text-muted-foreground">
                  Our logistics team coordinates crane offloading, job-site delivery windows, and terminal holds.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Button asChild variant="outline" className="border-border font-serif uppercase tracking-wider text-xs font-bold rounded-sm">
                  <Link to="/ContactPage">
                    <PhoneCall className="size-3.5 mr-1.5" />
                    Contact Logistics
                  </Link>
                </Button>
                <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 font-serif uppercase tracking-wider text-xs font-bold rounded-sm">
                  <Link to="/ShopPage">
                    View Catalog
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </motion.div>

      </div>
    </section>;
}